import React from 'react';
import ReactDOM from 'react-dom';
import { Table } from 'reactstrap';
import Moment from 'react-moment';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import { COLUMN_TYPES } from '../../../config/app.config';
import classNames from 'classnames';
import { getCloudinaryDeliveryUrl } from '../../../utils/cloudinaryDelivery';

const IMAGE_HOVER_THUMB_TRANSFORM = 'c_limit,w_160,h_192,q_auto,f_auto';
const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

function ImageHoverCell({ cellValue, column, rowData }) {
  const [visible, setVisible] = React.useState(false);
  const [popoverPos, setPopoverPos] = React.useState({ bottom: 0, left: 0 });
  const hideTimer = React.useRef(null);
  const triggerRef = React.useRef(null);

  const show = () => {
    clearTimeout(hideTimer.current);
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const itemWidth = 80 + 8;
      const popoverWidth = Math.min(cellValue.length * itemWidth + 16, window.innerWidth * 0.9);
      let left = rect.left + rect.width / 2 - popoverWidth / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - popoverWidth - 8));
      setPopoverPos({ bottom: window.innerHeight - rect.top + 6, left });
    }
    setVisible(true);
  };

  const hide = () => {
    hideTimer.current = setTimeout(() => setVisible(false), 200);
  };

  const handleClick = () => {
    if (column.onImageClick) {
      column.onImageClick(rowData);
    } else {
      console.log(cellValue);
    }
  };

  return (
    <div
      ref={triggerRef}
      style={{ display: 'inline-block' }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {visible && ReactDOM.createPortal(
        <div
          onMouseEnter={show}
          onMouseLeave={hide}
          style={{
            position: 'fixed',
            bottom: popoverPos.bottom,
            left: popoverPos.left,
            background: '#FEFEFE',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 4,
            padding: 8,
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            zIndex: 99999,
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          {cellValue.map((src, i) => (
            <img
              key={i}
              src={getCloudinaryDeliveryUrl(src, IMAGE_HOVER_THUMB_TRANSFORM)}
              alt=""
              loading="lazy"
              onClick={handleClick}
              style={{
                width: 80,
                height: 96,
                objectFit: 'cover',
                borderRadius: 2,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            />
          ))}
        </div>,
        document.body
      )}
      <button
        type="button"
        onClick={handleClick}
        className="nav-link-style"
      >
        {column.buttonText
          ? column.buttonText(cellValue.length)
          : `View (${cellValue.length})`}
      </button>
    </div>
  );
}

export function DataTable({
  columns,
  rows,
  tableClassName = 'loyal-table fixed-layout',
  isStriped = true,
  noDataMessage = 'No data available',
  noDataColSpan,
}) {
  const renderCellValue = (rowData, column) => {
    const cellValue = column.field ? rowData[column.field] : rowData;

    switch (column.type) {
      case COLUMN_TYPES.DATE:
        return cellValue ? (
          <Moment format={column.dateFormat || 'MM/DD/YYYY'}>
            {cellValue}
          </Moment>
        ) : (
          '-'
        );

      case COLUMN_TYPES.TIME:
        return cellValue ? (
          <Moment format={column.timeFormat || 'hh:mm A'}>{cellValue}</Moment>
        ) : (
          '-'
        );

      case COLUMN_TYPES.DATETIME:
        return cellValue ? (
          <Moment format={column.dateTimeFormat || 'MM/DD/YYYY hh:mm A'}>
            {cellValue}
          </Moment>
        ) : (
          '-'
        );

      case COLUMN_TYPES.CUSTOM:
        return column.renderCell
          ? column.renderCell(cellValue, rowData)
          : cellValue;

      case COLUMN_TYPES.TRUNCATED_TEXT:
        return cellValue ? (
            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              <ResponsiveEllipsis
                text={cellValue}
                maxLine={column.maxLines || 10}
                ellipsis={
                  column.ellipsisRenderer
                    ? column.ellipsisRenderer(cellValue, rowData)
                    : '...'
                }
                trimRight
                basedOn="letters"
              />
            </div>
        ) : (
          '-'
        );

      case COLUMN_TYPES.IMAGES:
        return cellValue?.length > 0 ? (
          <ImageHoverCell cellValue={cellValue} column={column} rowData={rowData} />
        ) : (
          '-'
        );

      case COLUMN_TYPES.PDFS:
        return cellValue?.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              if (column.onPdfClick) {
                column.onPdfClick(rowData);
              } else {
                console.log(cellValue);
              }
            }}
            className="nav-link-style"
          >
            {column.buttonText
              ? column.buttonText(cellValue.length)
              : `View (${cellValue.length})`}
          </button>
        ) : (
          '-'
        );

      case COLUMN_TYPES.TEXT:
      default:
        return cellValue || '-';
    }
  };

  return (
    <div className="scrollable-table">
      <Table striped={isStriped} className={tableClassName}>
        <thead>
          <tr className="header-tr" style={{ top: 0 }}>
            {columns.map((column, index) => (
              <th key={index} className={column.headerClassName}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows && rows.length > 0 ? (
            rows.map((rowData, rowIndex) => (
              <tr
                className={classNames('body-tr', rowData?.rowCustomClassName)}
                key={rowIndex}
              >
                {columns.map((column, colIndex) => (
                  <td key={colIndex}>{renderCellValue(rowData, column)}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={noDataColSpan || columns.length}
                className="text-center text-muted"
              >
                {noDataMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}
